/**
 * Nango integration unique keys shown in Connect UI.
 *
 * These must match the Unique Key of each integration configured in the
 * Nango dashboard (Integrations → …). Create each provider there before
 * clinicians can authorize it.
 *
 * Keep in sync with ml-scribe ``apis/v2/integrations/nango/providers.py``.
 *
 * Nango is a credential store only: it issues and refreshes OAuth tokens, and
 * every vendor call is made by Heidi's own credential broker. A provider may
 * therefore only appear here once the broker can route it, which today is
 * Google Workspace and Microsoft 365. Offering anything else would put a
 * connector in front of clinicians that the backend refuses to serve.
 */

export type NangoProviderCategory = 'workspace';

export const NANGO_ALLOWED_INTEGRATIONS = [
  // Microsoft 365
  'microsoft',
  'outlook',
  'microsoft-teams',
  'microsoft-word',
  'microsoft-excel',
  'one-note',
  'one-drive',
  'sharepoint-online',
  // Google Workspace
  'google-mail',
  'google-calendar',
  'google-drive',
  'google-docs',
  'google-sheet',
  'google-meet',
] as const;

export type NangoAllowedIntegration = (typeof NANGO_ALLOWED_INTEGRATIONS)[number];

export const NANGO_PROVIDER_CATEGORY: Record<
  NangoAllowedIntegration,
  NangoProviderCategory
> = {
  microsoft: 'workspace',
  outlook: 'workspace',
  'microsoft-teams': 'workspace',
  'microsoft-word': 'workspace',
  'microsoft-excel': 'workspace',
  'one-note': 'workspace',
  'one-drive': 'workspace',
  'sharepoint-online': 'workspace',
  'google-mail': 'workspace',
  'google-calendar': 'workspace',
  'google-drive': 'workspace',
  'google-docs': 'workspace',
  'google-sheet': 'workspace',
  'google-meet': 'workspace',
};

/**
 * Short “what Heidi can do” copy for the connectors catalog.
 *
 * Describes what Heidi can do once connected, not what the app itself does.
 * Keep in step with `NANGO_PROVIDER_CAPABILITIES` in `./capabilities.ts`.
 */
export const NANGO_PROVIDER_DESCRIPTIONS: Record<NangoAllowedIntegration, string> = {
  microsoft: 'Read and write mail, calendar, files, Teams, and OneNote via Microsoft 365',
  outlook: 'Search, read, draft, send, and reply to mail; manage calendar events',
  'microsoft-teams': 'Find teams, channels, and members; read and post conversations',
  'microsoft-word': 'Find and save Word documents',
  'microsoft-excel': 'Find and save Excel workbooks',
  'one-note': 'Create OneNote pages',
  'one-drive': 'Find, read, and save letters and files in OneDrive',
  'sharepoint-online': 'Find, read, and save SharePoint files',
  'google-mail': 'Search and read Gmail, create drafts, send messages, and reply',
  'google-calendar': 'List, create, update, and delete Google Calendar events',
  'google-drive': 'Find, read, and save letters and files in Google Drive',
  'google-docs': 'Read, create, and append to letters, protocols, and templates',
  'google-sheet': 'Read recall lists and audit trackers, and append rows',
  'google-meet': 'List recent Meet calls and read their transcripts',
};

export function getNangoProviderCategory(
  providerConfigKey: string
): NangoProviderCategory | undefined {
  return NANGO_PROVIDER_CATEGORY[providerConfigKey as NangoAllowedIntegration];
}

export function getNangoProviderDescription(providerConfigKey: string): string {
  return (
    NANGO_PROVIDER_DESCRIPTIONS[providerConfigKey as NangoAllowedIntegration] ??
    'Connect this app so Heidi can use it.'
  );
}
